/**
 * Permission mapping configuration
 * Maps page routes and API routes to permission slugs
 */

export interface PermissionMapping {
  path: string;
  permission?: string | null;
  name: string;
  description?: string;
}

export interface PermissionConfig {
  pages: PermissionMapping[];
  apis: PermissionMapping[];
}

// Page route permissions
export const PAGE_PERMISSIONS: PermissionMapping[] = [
  // Home
  {
    path: '/',
    permission: null,
    name: 'View Home',
    description: 'Access to the home page',
  },

  // Search — open to all
  {
    path: '/search',
    permission: null,
    name: 'View Search',
    description: 'Access to search functionality',
  },

  // Region — admin only
  {
    path: '/region',
    permission: 'admin.view',
    name: 'View Region',
    description: 'Access to region overview',
  },
  {
    path: '/region/[...slug]',
    permission: 'admin.view',
    name: 'View Region Detail',
    description: 'Access to region details',
  },

  // NACE — admin only
  {
    path: '/nace',
    permission: 'admin.view',
    name: 'View NACE',
    description: 'Access to NACE overview',
  },
  {
    path: '/industry',
    permission: null,
    name: 'View Industry',
    description: 'Access to Industry overview',
  },
  {
    path: '/industry/[code]/[slug]',
    permission: null,
    name: 'View Industry Detail',
    description: 'Access to Industry specific metrics',
  },
  {
    path: '/nace/[code]',
    permission: 'admin.view',
    name: 'View NACE Detail',
    description: 'Access to NACE code details',
  },

  // Analysis
  {
    path: '/analysis',
    permission: 'analysis.view',
    name: 'View Analysis',
    description: 'Access to analysis page',
  },

  // Companies
  {
    path: '/companies/[euId]',
    permission: 'companies.view',
    name: 'View Company Detail',
    description: 'Access to company details',
  },

  // Admin — all gated on settings.manage
  {
    path: '/admin',
    permission: 'admin.view',
    name: 'View Admin',
    description: 'Access to admin dashboard',
  },
  {
    path: '/admin/users',
    permission: 'admin.view',
    name: 'View Users',
    description: 'Access to user analytics and management',
  },
  {
    path: '/admin/crm',
    permission: 'admin.view',
    name: 'View CRM',
    description: 'M&A sales pipeline',
  },
  {
    path: '/admin/subscriptions',
    permission: 'admin.view',
    name: 'Manage Subscriptions',
    description: 'Access to subscription management',
  },
  {
    path: '/admin/stripe-events',
    permission: 'admin.view',
    name: 'View Stripe Events',
    description: 'Access to Stripe events',
  },
  {
    path: '/admin/logs',
    permission: 'admin.view',
    name: 'View Logs',
    description: 'Access to system logs',
  },
  {
    path: '/admin/log-stats',
    permission: 'admin.view',
    name: 'View Log Statistics',
    description: 'Access to log statistics dashboard',
  },
  {
    path: '/admin/search-analytics',
    permission: 'admin.view',
    name: 'View Search Analytics',
    description: 'Filter usage and zero-result analysis for /search',
  },
  {
    path: '/admin/referrers',
    permission: 'admin.view',
    name: 'View Referrers',
    description: 'Where visitors arrive from, by source over time',
  },
  {
    path: '/vergabe',
    permission: null,
    name: 'View Tenders',
    description: 'Access to TED tender notices',
  },
  {
    path: '/admin/settings',
    permission: 'admin.view',
    name: 'Manage Settings',
    description: 'Access to system settings',
  },
  {
    path: '/admin/website',
    permission: 'admin.view',
    name: 'Manage Company Websites',
    description: 'Access to company website management',
  },
  {
    path: '/admin/overrides',
    permission: 'admin.view',
    name: 'Manage Company Overrides',
    description: 'Access to company data override management',
  },
  {
    path: '/admin/files',
    permission: 'admin.view',
    name: 'Manage Company Files',
    description: 'Access to company file upload management',
  },
  {
    path: '/admin/coverage',
    permission: 'admin.view',
    name: 'View Coverage',
    description: 'Access to data coverage dashboard',
  },
  {
    path: '/admin/privacy',
    permission: 'admin.view',
    name: 'Manage Private Companies',
    description: 'Access to private company management',
  },
  {
    path: '/admin/api',
    permission: 'admin.view',
    name: 'Manage API Keys (Admin)',
    description: 'Create API keys for customers and view filtered usage',
  },
  {
    path: '/admin/credits',
    permission: 'admin.view',
    name: 'Manage Credits (Admin)',
    description: 'Inspect and grant v1 API / MCP credits for a user',
  },
  {
    path: '/admin/emails',
    permission: 'admin.view',
    name: 'Email Templates (Admin)',
    description: 'Preview every transactional email and send a test copy',
  },
  {
    path: '/admin/publications',
    permission: 'admin.view',
    name: 'Publications Desk (Admin)',
    description: 'Topics, LinkedIn post drafts and chart cards from FirmenData data',
  },
  {
    path: '/admin/publications/topics/[id]',
    permission: 'admin.view',
    name: 'Publication Topic (Admin)',
    description: 'Fact pack, charts and post generation for one topic',
  },
  {
    path: '/admin/publications/posts/[id]',
    permission: 'admin.view',
    name: 'Publication Post (Admin)',
    description: 'Post preview, revisions, validation and publishing for one post',
  },
  {
    path: '/admin/funnel',
    permission: 'admin.view',
    name: 'Funnel (Admin)',
    description: 'Visitor to paid conversion by week',
  },
  {
    path: '/admin/watchlist',
    permission: 'admin.view',
    name: 'Watchlist Overview (Admin)',
    description: 'Who monitors which companies, and on what plan',
  },
  // Account — open to all authenticated users (auth guard handles login redirect)
  {
    path: '/account',
    permission: null,
    name: 'View Account',
    description: 'Access to account section',
  },
  {
    path: '/account/billing',
    permission: null,
    name: 'Manage Billing',
    description: 'Access to billing management',
  },
  {
    path: '/account/api-keys',
    permission: null,
    name: 'View API Keys',
    description: 'Access to API keys (handler enforces per-user scoping)',
  },
  {
    path: '/account/usage',
    permission: null,
    name: 'View API Usage',
    description:
      'Per-day API request and credit usage (handler enforces per-user scoping)',
  },

  // Plans — public pricing page, visible to unauthenticated users
  {
    path: '/plans',
    permission: null,
    name: 'View Plans',
    description: 'Access to plans page',
  },

  // Insolvenzticker — open to all
  {
    path: '/insolvenzticker',
    permission: null,
    name: 'View Insolvenzticker',
    description: 'Access to Insolvenzticker page',
  },

  // Neugründungen — open to all
  {
    path: '/neugruendungen',
    permission: null,
    name: 'View New Companies',
    description: 'Access to newly registered GmbHs',
  },

  // Product keywords — admin only
  {
    path: '/products',
    permission: 'admin.view',
    name: 'View Product Keywords',
    description: 'Access to product keywords overview',
  },
  {
    path: '/products/[[...params]]',
    permission: 'admin.view',
    name: 'View Product Keyword Detail',
    description: 'Access to product keyword detail pages',
  },

  // FAQ — open to all
  {
    path: '/faq',
    permission: null,
    name: 'View FAQ',
    description: 'Access to FAQ page',
  },
  {
    path: '/sources',
    permission: null,
    name: 'View Data Sources',
    description: 'Access to the data sources transparency page',
  },

  // Documentation — open to all
  {
    path: '/docs',
    permission: null,
    name: 'View Docs',
    description: 'Documentation index',
  },
  {
    path: '/docs/lists-import',
    permission: null,
    name: 'View List Import Docs',
    description: 'Documentation for the list import feature',
  },

  // APIs
  {
    path: '/developer/apis',
    permission: null,
    name: 'View APIs',
    description: 'Access to APIs overview page',
  },
  {
    path: '/developer/sdks',
    permission: null,
    name: 'View SDKs',
    description: 'Access to official SDKs overview page',
  },

  // Developer
  {
    path: '/developer/subscriptions',
    permission: null,
    name: 'View Subscriptions & Events',
    description:
      'Access to subscription event history (handler enforces per-user scoping)',
  },

  // Overview — open to all authenticated users.
  {
    path: '/overview',
    permission: null,
    name: 'View Overview',
    description: 'Access to the personal overview page',
  },
  // Activity — open to all authenticated users (the page enforces auth).
  {
    path: '/activity',
    permission: null,
    name: 'View Activity',
    description: 'Recent searches and company views (page enforces auth)',
  },
  // Watchlist — open to all authenticated users (the handler enforces auth).
  {
    path: '/watchlist',
    permission: null,
    name: 'View Watchlist',
    description: 'Access to the personal company watchlist page',
  },
  // My Lists (Pro) — page is open to all authenticated users so free users
  // see the upsell; creating/using lists is plan-gated in the API routes.
  {
    path: '/my-lists',
    permission: null,
    name: 'View My Lists',
    description: 'Overview of the personal company lists',
  },
  {
    path: '/my-lists/[listId]',
    permission: null,
    name: 'View List',
    description: 'One personal company list (handler enforces ownership)',
  },
  {
    path: '/account/notifications',
    permission: null,
    name: 'View Notifications',
    description: 'Full notification history (handler scopes to the caller)',
  },
  {
    path: '/account/downloads',
    permission: null,
    name: 'View Downloads',
    description:
      'Documents the user has downloaded (handler scopes to the caller)',
  },
];

// API route permissions
export const API_PERMISSIONS: PermissionMapping[] = [
  // Search
  {
    path: '/api/search/metrics',
    permission: 'admin.view',
    name: 'Use Search Metrics API',
    description: 'Access to search metrics API',
  },

  // Analytics
  {
    path: '/api/analytics/company-tax-rates',
    permission: 'metrics.view',
    name: 'View Company Tax Rates',
    description: 'Access to company tax rates API',
  },
  {
    path: '/api/analytics/balance-distribution',
    permission: 'metrics.view',
    name: 'View Balance Distribution',
    description: 'Access to balance distribution API',
  },
  {
    path: '/api/analytics/revenue-by-nace',
    permission: 'metrics.view',
    name: 'View Revenue by NACE',
    description: 'Access to revenue by NACE API',
  },
  {
    path: '/api/analytics/aggregated-revenue',
    permission: 'metrics.view',
    name: 'View Aggregated Revenue',
    description: 'Access to aggregated revenue API',
  },
  {
    path: '/api/analytics/company-cities',
    permission: 'metrics.view',
    name: 'View Company Cities',
    description: 'Access to company cities API',
  },
  {
    path: '/api/analytics/ai-cost-hourly',
    permission: 'metrics.view',
    name: 'View AI Cost Hourly',
    description: 'Access to AI cost hourly API',
  },

  // NACE / Industry
  {
    path: '/api/nace/coverage',
    permission: 'nace.view',
    name: 'View NACE Coverage',
    description: 'Access to NACE coverage API',
  },
  {
    path: '/api/industries',
    permission: 'industry.view',
    name: 'View Industries',
    description: 'Access to industries API',
  },
  {
    path: '/api/industries/[code]/details',
    permission: 'industry.view',
    name: 'View Industry Details',
    description: 'Access to industry specific analytics',
  },
  {
    path: '/api/nace/[code]/stats',
    permission: 'nace.view',
    name: 'View NACE Stats',
    description: 'Access to NACE stats API',
  },
  {
    path: '/api/nace/[code]/companies',
    permission: 'nace.view',
    name: 'View NACE Companies',
    description: 'Access to NACE companies API',
  },
  {
    path: '/api/nace_match',
    permission: 'nace.user',
    name: 'Use NACE Match',
    description: 'Access to NACE match API',
  },

  // Map
  {
    path: '/api/map/nace-sectors',
    permission: 'map.view',
    name: 'View NACE Sectors',
    description: 'Access to NACE sectors API',
  },
  {
    path: '/api/map/nace-subsectors',
    permission: 'map.view',
    name: 'View NACE Subsectors',
    description: 'Access to NACE subsectors API',
  },

  // Analysis
  {
    path: '/api/analysis',
    permission: 'analysis.view',
    name: 'Use Analysis API',
    description: 'Access to analysis API',
  },

  // Admin APIs
  {
    path: '/api/admin/users',
    permission: 'admin.view',
    name: 'View Users API',
    description: 'Access to user analytics and search API',
  },
  {
    path: '/api/admin/users/retention',
    permission: 'admin.view',
    name: 'User Retention API',
    description: 'Weekly signup-cohort retention grid',
  },
  {
    path: '/api/admin/users/[sub]',
    permission: 'admin.view',
    name: 'Manage User API',
    description: 'Hard-delete a user and override their tier',
  },
  {
    path: '/api/admin/crm',
    permission: 'admin.view',
    name: 'CRM API',
    description: 'M&A sales pipeline API',
  },
  {
    path: '/api/admin/crm/[id]',
    permission: 'admin.view',
    name: 'CRM Lead API',
    description: 'M&A sales pipeline single-lead API',
  },
  {
    path: '/api/admin/referrers',
    permission: 'admin.view',
    name: 'View Referrers API',
    description: 'Referrer source statistics API',
  },
  {
    path: '/api/admin/subscriptions',
    permission: 'admin.subscriptions',
    name: 'Manage Subscriptions API',
    description: 'Access to subscriptions API',
  },
  {
    path: '/api/admin/stripe-events',
    permission: 'admin.stripe-events',
    name: 'View Stripe Events API',
    description: 'Access to Stripe events API',
  },

  {
    path: '/api/account',
    permission: null,
    name: 'View Own Account API',
    description: 'Access to own account API (all authenticated users)',
  },
  {
    path: '/api/account/profile',
    permission: null,
    name: 'Update Own Account Profile API',
    description: 'Access to own account profile API (all authenticated users)',
  },
  {
    path: '/api/tenders',
    permission: null,
    name: 'View Tenders API',
    description: 'Access to TED tender notices API (all authenticated users)',
  },
  {
    path: '/api/admin/settings/maintenance',
    permission: 'admin.view',
    name: 'Manage Maintenance Mode API',
    description: 'Toggle site-wide maintenance mode',
  },
  {
    path: '/api/admin/website',
    permission: 'admin.view',
    name: 'Manage Company Websites API',
    description: 'Access to company website management API',
  },
  {
    path: '/api/admin/overrides',
    permission: 'admin.view',
    name: 'Manage Company Overrides API',
    description: 'Access to company override management API',
  },
  {
    path: '/api/admin/files',
    permission: 'admin.view',
    name: 'Manage Company Files API',
    description: 'Access to company file upload management API',
  },
  {
    path: '/api/admin/coverage',
    permission: 'admin.view',
    name: 'View Coverage API',
    description: 'Access to data coverage metrics API',
  },
  {
    path: '/api/admin/privacy',
    permission: 'admin.view',
    name: 'Manage Private Companies API',
    description: 'Access to private company management API',
  },
  {
    path: '/api/admin/api',
    permission: 'admin.view',
    name: 'Manage API Keys (Admin)',
    description: 'Create and list API keys across all customers',
  },
  {
    path: '/api/admin/api/[id]',
    permission: 'admin.view',
    name: 'Revoke API Key (Admin)',
    description: 'Revoke any API key',
  },
  {
    path: '/api/admin/api/usage',
    permission: 'admin.view',
    name: 'View API Usage (Admin)',
    description: 'Aggregated and per-customer/per-sub API usage',
  },
  {
    path: '/api/admin/api/logs',
    permission: 'admin.view',
    name: 'View API Request Log (Admin)',
    description: 'Per-call request log filtered by sub, customer, or key',
  },
  {
    path: '/api/admin/credits',
    permission: 'admin.view',
    name: 'Grant Credits (Admin)',
    description: 'Grant or reset credits for any sub',
  },
  {
    path: '/api/admin/emails/preview',
    permission: 'admin.view',
    name: 'Preview Emails (Admin)',
    description: 'Render a transactional email template to HTML',
  },
  {
    path: '/api/admin/emails/send-test',
    permission: 'admin.view',
    name: 'Send Test Email (Admin)',
    description: 'Send one template through the real mail transport',
  },
  {
    path: '/api/admin/emails/log',
    permission: 'admin.view',
    name: 'View Sent Email Log (Admin)',
    description: 'List the emails actually sent to users',
  },
  {
    path: '/api/admin/emails/log/[id]',
    permission: 'admin.view',
    name: 'View Sent Email Body (Admin)',
    description: 'Read one logged email as the recipient received it',
  },
  {
    path: '/api/admin/publications/overview',
    permission: 'admin.view',
    name: 'Publications Overview (Admin)',
    description: 'Regular and proposed topics, running job, spend today',
  },
  {
    path: '/api/admin/publications/topics/generate',
    permission: 'admin.view',
    name: 'Generate Publication Topics (Admin)',
    description: 'Start a topic-generation job',
  },
  {
    path: '/api/admin/publications/topics/[id]',
    permission: 'admin.view',
    name: 'View Publication Topic (Admin)',
    description: 'Topic with fact pack and probe',
  },
  {
    path: '/api/admin/publications/topics/[id]/dismiss',
    permission: 'admin.view',
    name: 'Dismiss Publication Topic (Admin)',
    description: 'Dismiss a proposed topic',
  },
  {
    path: '/api/admin/publications/topics/[id]/pack',
    permission: 'admin.view',
    name: 'Rebuild Publication Fact Pack (Admin)',
    description: 'Force a fact-pack rebuild for a topic',
  },
  {
    path: '/api/admin/publications/topics/[id]/posts',
    permission: 'admin.view',
    name: 'Create Publication Post (Admin)',
    description: 'Start a post-writing job for a topic',
  },
  {
    path: '/api/admin/publications/posts/[id]',
    permission: 'admin.view',
    name: 'View Publication Post (Admin)',
    description: 'Post with all revisions',
  },
  {
    path: '/api/admin/publications/posts/[id]/regenerate',
    permission: 'admin.view',
    name: 'Regenerate Publication Post (Admin)',
    description: 'Write a new revision of a post',
  },
  {
    path: '/api/admin/publications/posts/[id]/mark-published',
    permission: 'admin.view',
    name: 'Mark Publication Published (Admin)',
    description: 'Record that a post went out on LinkedIn',
  },
  {
    path: '/api/admin/publications/jobs/[id]',
    permission: 'admin.view',
    name: 'View Publication Job (Admin)',
    description: 'Poll a topic or post job',
  },
  {
    path: '/api/admin/publications/settings',
    permission: 'admin.view',
    name: 'Publication Settings (Admin)',
    description: 'Voice samples, banlist and daily spend cap',
  },
  {
    path: '/api/admin/publications/history',
    permission: 'admin.view',
    name: 'Publication History (Admin)',
    description: 'Published posts',
  },
  {
    path: '/api/admin/credits/grant-all',
    permission: 'admin.view',
    name: 'Bulk Grant Credits (Admin)',
    description: 'Grant credits to every user profile in one call',
  },
  {
    path: '/api/admin/credits/[sub]',
    permission: 'admin.view',
    name: 'Inspect Credits (Admin)',
    description: "View a sub's credit balance and ledger",
  },
  {
    path: '/api/admin/funnel',
    permission: 'admin.view',
    name: 'Funnel Data (Admin)',
    description: 'Weekly conversion counts across the funnel',
  },
  {
    path: '/api/admin/credits/reconcile',
    permission: 'admin.view',
    name: 'Reconcile Top-ups (Admin)',
    description: 'Stripe top-up payments with no matching credit grant',
  },
  {
    path: '/api/admin/credits/users',
    permission: 'admin.view',
    name: 'List Credit Accounts (Admin)',
    description: 'Paginated users with plan, balance and lifetime spend',
  },
  {
    path: '/api/admin/watchlist',
    permission: 'admin.view',
    name: 'List Watchers (Admin)',
    description: 'Users holding at least one watched company',
  },
  {
    path: '/api/admin/watchlist/[sub]',
    permission: 'admin.view',
    name: 'Inspect Watchlist (Admin)',
    description: 'The companies a given user watches',
  },

  // Developer / Subscription Event APIs — open to all authenticated users;
  // each handler enforces per-user scoping.
  {
    path: '/api/developer/subscriptions/events',
    permission: null,
    name: 'View Subscription Events API',
    description: 'Access to subscription event history API',
  },
  {
    path: '/api/developer/subscriptions/events/[eventId]',
    permission: null,
    name: 'View Subscription Event Detail API',
    description: 'Access to single subscription event detail',
  },
  {
    path: '/api/developer/subscriptions/events/[eventId]/resend',
    permission: null,
    name: 'Resend Subscription Event API',
    description: 'Re-deliver a subscription event',
  },
  {
    path: '/api/developer/subscriptions',
    permission: null,
    name: 'List Subscriptions API',
    description: 'List the current user’s subscriptions',
  },
  {
    path: '/api/developer/subscriptions/[subscriptionId]',
    permission: null,
    name: 'Delete Subscription API',
    description: 'Delete one of the current user’s subscriptions',
  },

  // Stripe APIs (these may need special handling, but include for completeness)
  {
    path: '/api/stripe/create-checkout-session',
    permission: 'account.billing',
    name: 'Create Checkout Session',
    description: 'Access to create checkout session',
  },
  {
    path: '/api/stripe/create-portal-session',
    permission: 'account.billing',
    name: 'Create Portal Session',
    description: 'Access to create portal session',
  },
  {
    path: '/api/stripe/invoices',
    permission: 'account.billing',
    name: 'View Invoices',
    description: 'Access to invoices API',
  },
  {
    path: '/api/stripe/products',
    permission: 'admin.view',
    name: 'View Products',
    description: 'Access to products API',
  },
  {
    path: '/api/stripe/subscription',
    permission: 'account.billing',
    name: 'Manage Subscription',
    description: 'Access to subscription API',
  },
  {
    path: '/api/stripe/verify-session',
    permission: 'account.billing',
    name: 'Verify Session',
    description: 'Access to verify session API',
  },

  // Company APIs - Unified Endpoints (companies.view permission)
  {
    path: '/api/companies/[euId]/unified/basic',
    permission: null,
    name: 'View Company Basic Info',
    description: 'Public access to unified company basic information API',
  },
  {
    path: '/api/companies/[euId]/dataset',
    permission: null,
    name: 'View Company Dataset',
    description:
      'Public machine-readable dataset of company basic info (referenced from schema.org Dataset JSON-LD)',
  },
  {
    path: '/api/companies/[euId]/unified/register',
    permission: null,
    name: 'View Company Register Info',
    description:
      'Access to unified company register information API (all logged-in users)',
  },
  {
    path: '/api/companies/[euId]/unified/financials',
    permission: null,
    name: 'View Company Financials',
    description:
      'Access to unified company financial data API (all logged-in users)',
  },
  {
    path: '/api/companies/[euId]/financial-tables/raw',
    permission: 'companies.view',
    name: 'View Company Raw Financials',
    description: 'Access to company raw financial data API',
  },

  // Company APIs - Contact
  {
    path: '/api/companies/[euId]/unified/contact',
    permission: null,
    name: 'View Company Contact',
    description: 'Access to unified company contact information API',
  },
  {
    path: '/api/companies/[euId]/unified/gesellschafter',
    permission: null,
    name: 'View Company Gesellschafter',
    description: 'Access to unified company gesellschafter information API',
  },

  // Company APIs - Register docs & Publications (all logged-in users)
  {
    path: '/api/companies/[euId]/network',
    permission: null,
    name: 'View Company Network',
    description: 'Access to company relationship network graph',
  },
  {
    path: '/api/person',
    permission: null,
    name: 'View Person Profile',
    description: 'Access to person profile and their company connections',
  },
  {
    path: '/api/companies/[euId]/files',
    permission: null,
    name: 'View Company Files',
    description:
      'Access to company publications listing API (all logged-in users)',
  },
  {
    path: '/api/companies/[euId]/files/[fileId]/download',
    permission: null,
    name: 'Download Company File',
    description:
      'Access to download company publications (all logged-in users)',
  },
  {
    path: '/api/companies/[euId]/register-file/download',
    permission: null,
    name: 'Download Register File',
    description:
      'Scrape and download a single register file type (all logged-in users)',
  },
  {
    path: '/api/companies/[euId]/files/[fileId]/content',
    permission: null,
    name: 'View Company File Content',
    description: 'Access to view company file content (all logged-in users)',
  },

  // API Key management — open to all authenticated users; handlers scope by user.
  {
    path: '/api/account/api',
    permission: null,
    name: 'Manage API Keys',
    description: 'Create and list API keys',
  },
  {
    path: '/api/account/api/[id]',
    permission: null,
    name: 'Modify API Key',
    description: 'Revoke or update an API key',
  },
  {
    path: '/api/account/usage',
    permission: null,
    name: 'View API Usage',
    description:
      'Per-day API request and credit usage (handler enforces per-user scoping)',
  },

  // Lists APIs — any authenticated user; plan caps enforced in the handlers
  // (the plan lives in the auth DB, which the backend can't see).
  {
    path: '/api/lists',
    permission: null,
    name: 'My Lists',
    description: "List or create the authenticated user's company lists",
  },
  {
    path: '/api/lists/[listId]',
    permission: null,
    name: 'Modify List',
    description: 'Read, rename, configure or delete one owned list',
  },
  {
    path: '/api/lists/[listId]/companies/[euId]',
    permission: null,
    name: 'Modify List Companies',
    description: 'Add or remove a company on one owned list',
  },
  {
    path: '/api/lists/[listId]/companies/bulk',
    permission: null,
    name: 'Bulk Add List Companies',
    description: 'Import-wizard commit: add many companies to one owned list',
  },
  {
    path: '/api/lists/import/match',
    permission: null,
    name: 'Match List Import Rows',
    description: 'Match spreadsheet rows to companies for the import wizard',
  },
  {
    path: '/api/lists/import/fetch-url',
    permission: null,
    name: 'Fetch List Import File',
    description: 'Server-side fetch of a CSV/XLSX URL for the import wizard',
  },
  {
    path: '/api/lists/[listId]/share',
    permission: null,
    name: 'Share List',
    description: 'Mint or revoke the public share token of one owned list',
  },
  {
    path: '/api/lists/[listId]/watch',
    permission: null,
    name: 'List Watch',
    description: 'Configure change alerts for one owned list',
  },

  // Saved-search APIs — any authenticated user. Unlike Lists there is no
  // Pro gate: every tier may save, only the count differs, so the cap is
  // enforced per-handler rather than by a feature check.
  {
    path: '/api/saved-searches',
    permission: null,
    name: 'Saved Searches',
    description: "List or create the authenticated user's saved searches",
  },
  {
    path: '/api/saved-searches/[id]',
    permission: null,
    name: 'Modify Saved Search',
    description: 'Rename, repoint or delete one owned saved search',
  },
  {
    path: '/api/saved-searches/[id]/opened',
    permission: null,
    name: 'Open Saved Search',
    description: 'Advance the new-matches baseline after viewing results',
  },
  {
    path: '/api/saved-searches/[id]/to-list',
    permission: null,
    name: 'Saved Search To List',
    description: 'Materialise a saved search as a company list (Pro)',
  },
  {
    path: '/api/saved-searches/refresh-counts',
    permission: null,
    name: 'Refresh Saved Search Counts',
    description: 'Remeasure match counts for a batch of saved searches',
  },

  // Watchlist APIs — any authenticated user; backend keys entries by Auth0 sub.
  {
    path: '/api/watch',
    permission: null,
    name: 'Watch Overview',
    description: 'Everything the user watches — companies and lists — plus the tier budget',
  },
  {
    path: '/api/watchlist',
    permission: null,
    name: 'View Watchlist',
    description: "List the authenticated user's company watchlist",
  },
  {
    path: '/api/watch/company/[euId]',
    permission: null,
    name: 'Configure Company Watch',
    description:
      'Read or replace the alert criteria, recipients and cadence for one watched company',
  },
  {
    path: '/api/watchlist/[euId]',
    permission: null,
    name: 'Modify Watchlist',
    description:
      "Add or remove a company from the authenticated user's watchlist",
  },
  {
    path: '/api/watchlist/notifications',
    permission: null,
    name: 'View Watchlist Notifications',
    description: "List the authenticated user's watchlist notifications",
  },
  {
    path: '/api/notifications',
    permission: null,
    name: 'View Notifications',
    description:
      'List every notification for the authenticated user (the bell)',
  },
  {
    path: '/api/notifications/[notificationId]',
    permission: null,
    name: 'Mark Notification Read',
    description: 'Mark a single notification as read',
  },
  {
    path: '/api/notifications/read-all',
    permission: null,
    name: 'Mark All Notifications Read',
    description: 'Mark every notification for the caller as read',
  },
  {
    path: '/api/account/notification-settings',
    permission: null,
    name: 'Notification Settings',
    description:
      "Read and update the caller's alert-email, credit-email and email-language settings",
  },

  // Unified alerts — any authenticated user; the backend keys every
  // subscription by Auth0 sub and prices it in credits per day.
  {
    path: '/api/alerts/pricing',
    permission: null,
    name: 'Alert Pricing',
    description: 'Daily credit rates for company, list and feed alerts',
  },
  {
    path: '/api/alerts/cost',
    permission: null,
    name: 'Alert Cost',
    description: "The caller's daily alert cost and how long the balance lasts",
  },
  {
    path: '/api/alerts/quote',
    permission: null,
    name: 'Alert Quote',
    description: 'Price a proposed or edited alert before saving it',
  },
  {
    path: '/api/alerts/subscriptions',
    permission: null,
    name: 'Alert Subscriptions',
    description: "List the caller's alerts or create a feed alert",
  },
  {
    path: '/api/alerts/subscriptions/[publicId]',
    permission: null,
    name: 'Modify Alert Subscription',
    description: 'Edit, pause, resume or delete one owned alert',
  },
  {
    path: '/api/alerts/settings',
    permission: null,
    name: 'Alert Settings',
    description: "Read and update the caller's backend alert-mail settings",
  },
  {
    path: '/api/account/downloads',
    permission: null,
    name: 'View Downloads',
    description: "List the authenticated user's downloaded documents",
  },
  {
    path: '/api/companies/logos',
    permission: null,
    name: 'Company Logos',
    description: 'Batch company logos for guests and authenticated users',
  },
  {
    path: '/api/companies/newly-founded',
    permission: null,
    name: 'Newly Founded Companies',
    description: 'Public feed of newly founded companies',
  },
  {
    path: '/api/account/recent-views',
    permission: null,
    name: 'Recent Company Views',
    description: "List or record the authenticated user's recent company views",
  },
  {
    path: '/api/account/recent-searches',
    permission: null,
    name: 'Recent Searches',
    description: "List or record the authenticated user's recent searches",
  },
  {
    path: '/api/documents/status',
    permission: null,
    name: 'Document Status',
    description:
      'Current version / withdrawal state of documents, by resource key',
  },
  {
    path: '/api/watchlist/notifications/[notificationId]',
    permission: null,
    name: 'Mark Watchlist Notification Read',
    description: 'Mark a single watchlist notification as read',
  },
  {
    path: '/api/watchlist/notifications/read-all',
    permission: null,
    name: 'Mark All Watchlist Notifications Read',
    description: 'Mark every watchlist notification as read',
  },
];

/**
 * Get permission for a page route
 */
export function getPagePermission(path: string): string | null | undefined {
  // Exact match first
  const exact = PAGE_PERMISSIONS.find((p) => p.path === path);
  if (exact) return exact.permission;

  // Pattern match for dynamic routes
  for (const mapping of PAGE_PERMISSIONS) {
    if (mapping.path.includes('[')) {
      const pattern = mapping.path.replace(/\[.*?\]/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(path)) {
        return mapping.permission;
      }
    }
  }

  return null;
}

/**
 * Get permission for an API route
 */
export function getApiPermission(path: string): string | null | undefined {
  // Exact match first
  const exact = API_PERMISSIONS.find((p) => p.path === path);
  if (exact) return exact.permission;

  // Pattern match for dynamic routes
  for (const mapping of API_PERMISSIONS) {
    if (mapping.path.includes('[')) {
      const pattern = mapping.path.replace(/\[.*?\]/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(path)) {
        return mapping.permission;
      }
    }
  }

  return null;
}

/**
 * Get all permissions
 */
export function getAllPermissions(): PermissionMapping[] {
  return [...PAGE_PERMISSIONS, ...API_PERMISSIONS];
}
